import { mevStorage } from '../../mev/utils/mevStorage'

export type PrintStyle = {
    mode: 'Image' | 'Text'
    brand: 'ESCPOS' | 'StarLine' | 'StarImpact'
    starEscpos: boolean
    fontWidth: number
    fontHeight: number
}

/** Star Line commands. The ESC/POS checkbox keeps the existing command path. */
export function useStarLine(style: Pick<PrintStyle, 'brand' | 'starEscpos'>): boolean {
    return style.brand === 'StarLine' && !style.starEscpos
}

function resolveStoreId(): string | null {
    const envirn = (localStorage.getItem('envirn') || 'DEV').toLowerCase()
    return mevStorage.getStoreId()
        || localStorage.getItem(`store_id_${envirn}`)
        || null
}

function printerApiKey(printer: any): string {
    return String(
        printer?.generalTab?.apikey
        || printer?.generalTab?.apiKey
        || printer?.apikey
        || printer?.api_key
        || printer?.data?.generalTab?.apikey
        || printer?.data?.api_key
        || ''
    )
}

function printerSettingsTab(printer: any): any {
    return printer?.settingsTab || printer?.data?.settingsTab || null
}

function clampPrintSize(value: unknown): number {
    const parsed = parseInt(String(value ?? ''), 10)
    if (!Number.isFinite(parsed)) return 1
    return Math.min(8, Math.max(1, parsed))
}

/**
 * Read ESCPOS printing type for one virtual printer.
 * Image stays the canvas path. Text prints ESC/POS characters.
 */
export async function loadPrinterPrintStyle(apiKey?: string): Promise<PrintStyle> {
    const storeId = resolveStoreId()
    let source = 'missing'
    let settingsTab: any
    let printerType: unknown
    let printerBrand: unknown
    let starEscpos: unknown
    const wanted = apiKey || ''

    try {
        const offlineDB = (await import('../indexedDB/offlineDB')).default
        if (storeId && wanted) {
            const storeData = await offlineDB.getMasterData('stores', storeId)
            const printerSettings = storeData?.printerSettings || storeData?.data?.printerSettings || []
            const match = printerSettings.find((printer: any) => printerApiKey(printer) === wanted)
            if (match) {
                settingsTab = printerSettingsTab(match)
                printerType = settingsTab?.printerType
                printerBrand = settingsTab?.printer
                starEscpos = settingsTab?.starEscpos
                source = 'indexeddb'
            } else {
                console.log('[Print style] printer not in store printerSettings', {
                    apiKey: wanted,
                    storeId,
                    apikeys: printerSettings.map((printer: any) => printerApiKey(printer)),
                })
            }
        }
    } catch (error) {
        console.warn('[Print style] Could not load from IndexedDB:', error)
    }

    if (!settingsTab && wanted) {
        try {
            const allPrinters = (await window.api.findPrinters()) as any[]
            const match = allPrinters.find((printer: any) => printerApiKey(printer) === wanted)
            if (match) {
                settingsTab = printerSettingsTab(match)
                printerType = settingsTab?.printerType || match?.printerType || match?.data?.printerType
                printerBrand = settingsTab?.printer || match?.printer
                starEscpos = settingsTab?.starEscpos
                source = 'nedb'
            }
        } catch (error) {
            console.warn('[Print style] Could not load from NeDB:', error)
        }
    }

    const brandName = String(printerBrand || '').toUpperCase()
    const brand: PrintStyle['brand'] = brandName === 'STARLINE' || brandName === 'STAR' ? 'StarLine' : 'ESCPOS'
    const style: PrintStyle = {
        mode: String(printerType || '').toLowerCase() === 'text' ? 'Text' : 'Image',
        brand,
        starEscpos: brand === 'StarLine' && (starEscpos === true || String(starEscpos).toLowerCase() === 'true'),
        fontWidth: clampPrintSize(settingsTab?.fontWidth),
        fontHeight: clampPrintSize(settingsTab?.fontHeight),
    }
    console.log('[Print style] value', {
        apiKey: wanted || '(none)',
        storeId,
        source,
        printer: style.brand,
        starEscpos: style.starEscpos,
        printerType: printerType ?? '(missing)',
        mode: style.mode,
        fontWidth: style.fontWidth,
        fontHeight: style.fontHeight,
    })
    return style
}
